import { useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import { Column } from 'common/components/BasicTable/types'
import { DateRange } from 'common/components/DateRangePicker'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { FeatureKey } from 'common/constants/permission'
import {
  getCostKey,
  getCostTransactionsHistoryInfiniteKey,
  getMedicineNameKey,
  getMedicinePieChartKey,
  getMedicinesByRangeKey,
  getMedicineSummaryTodayKey,
} from 'common/constants/queryKeys'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { formatThaiShortDateTwoDigitYear, formatTime } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { dashboardDateDisplayValue } from 'modules/food/utils'
import { useMutateDeleteMedicineByProduct } from 'modules/medicalSupply/api/mutation'
import { useGetMedicineNames, useGetMedicinePieChart } from 'modules/medicalSupply/api/queries'
import { useMedicines, useMedicinesSummaryToday } from 'modules/medicalSupply/api/selector'
import { selectedMedicineIdState } from 'modules/medicalSupply/stores/atom'
import { useSpeciesName } from 'modules/pond/api/selector'
import { normalizeSpeciesName } from 'utils/analytics'
import MoreAction from 'modules/pond/components/MoreAction'
import { useSnackbar } from 'notistack'
import { ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useRecoilState } from 'recoil'

import { Box, Tooltip } from '@mui/material'

import MedicalSupplyDialog from '../MedicalSupplyDialog'

const MedicalSupplyTable = () => {
  const { t } = useTranslation('medicalsupply')
  const qc = useQueryClient()

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  })
  const speciesName = useSpeciesName()
  const { enqueueSnackbar } = useSnackbar()

  const [open, setOpen] = useState(false)

  const deleteMedicineMutation = useMutateDeleteMedicineByProduct()
  const { trackEvent } = useAnalytics()

  const [medicineId, setMedicineId] = useRecoilState(selectedMedicineIdState)

  const { data, isFetching, fetchNextPage, hasNextPage } = useMedicines(
    'table',
    dateRange.startDate ?? undefined,
    dateRange.endDate ?? undefined
  )

  const onSuccess = (res: AxiosResponse<any>) => {
    const { status } = res.data
    if (status === 200) {
      enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
      qc.invalidateQueries({ queryKey: getMedicinesByRangeKey.all() })
      qc.invalidateQueries({ queryKey: getMedicinePieChartKey.all() })
      qc.invalidateQueries({ queryKey: getMedicineNameKey.all() })
      qc.invalidateQueries({ queryKey: getMedicineSummaryTodayKey.all() })
      qc.invalidateQueries({ queryKey: getCostKey.all() })
      qc.invalidateQueries({ queryKey: getCostTransactionsHistoryInfiniteKey.all() })
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(
        AnalyticsEvents.medicineRecordDeleted,
        normalizedSpecies ? { species: normalizedSpecies } : undefined
      )
    }
  }

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      await deleteMedicineMutation.mutateAsync(Number(medicineId), { onSuccess })
    },
  })

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpenEdit = (id: number) => {
    setMedicineId(id)
    setOpen(true)
  }

  const handleOpenDeleteConfirm = (id: number) => {
    setMedicineId(id)
    openConfirmDialog()
  }

  const onLoadMoreRows = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  const columns: Column[] = [
    {
      id: 'datetime',
      title: t('date.time.save'),
      width: '150px',
    },
    {
      id: 'shrimpAge',
      title: <Trans i18nKey={'pond:animal.age'} values={{ species: speciesName }} />,
      width: '100px',
    },
    {
      id: 'name',
      title: t('medicine'),
      width: '150px',
    },
    {
      id: 'lotNumber',
      title: t('medicine.lot'),
      width: '150px',
    },
    {
      id: 'unit',
      title: t('unit'),
      width: '150px',
    },
    {
      id: 'amount',
      title: t('amount'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'acc',
      title: t('accumulate.kg'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'medicineId',
      title: '',
      width: '100px',
    },
  ]

  const createTableRows = (
    datetime: string,
    shrimpAge: number,
    name: ReactNode,
    lotNumber: string,
    unit: string,
    amount: number,
    acc: number,
    medicineId: number
  ) => ({
    datetime: (
      <Box sx={{ whiteSpace: 'pre-line' }}>
        {formatThaiShortDateTwoDigitYear(datetime) + '\n' + formatTime(datetime)}
      </Box>
    ),
    shrimpAge,
    name,
    lotNumber,
    unit,
    amount,
    acc,
    medicineId: (
      <MoreAction
        featureKey={FeatureKey.Medicine}
        isHideOnCompleted={false}
        onClickEdit={() => handleOpenEdit(medicineId)}
        onClickDelete={() => handleOpenDeleteConfirm(medicineId)}
      />
    ),
  })

  const rows = data.map((el) => {
    const productName = el.product
      ? `${el.product.name}${el.product.stock_deleted_at ? ` ${t('deleted_suffix')}` : ''}`
      : '-'

    return createTableRows(
      el.datetime,
      el.shrimpAge,
      <Tooltip title={productName} arrow >
        <span>{productName}</span>
      </Tooltip>,
      el.product?.lot_number || '-',
      el.unit.name,
      el.amount,
      el.accumulatedMedicine,
      el.id
    )
  })

  return (
    <>
      {dialog}
      <BaseTableCard
        title={t('medicine.summary')}
        refresh={() => {
          qc.invalidateQueries({ queryKey: getMedicinesByRangeKey.all() })
        }}
        isLoading={isFetching}
        isEmpty={data.length === 0}
        isDateRangePicker={true}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        displayValue={dashboardDateDisplayValue}
        disableRefresh={true}
      >
        <BasicTable
          columns={columns}
          onLoading={onLoadMoreRows}
          isLoading={isFetching}
          rows={rows}
        />
      </BaseTableCard>
      <MedicalSupplyDialog onClose={handleClose} open={open} isEdit />
    </>
  )
}

export default MedicalSupplyTable
